/* eslint-disable react-hooks/exhaustive-deps */
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Input,
	ListItem,
	SimpleGrid,
	Text,
	UnorderedList,
	WrapItem,
} from '@chakra-ui/react';
import { faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import DownloadAndViewButton from '../../../../components/Files/DownloadAndViewButton';
import TempFileUploader from '../../../../components/Files/TempFileUploader';
import LabelDetail from '../../../../components/LabelDetail';
import Tip from '../../../../components/Tip/Tip';

export default function AdditionalAttachment({
	initData = [],
	isEditing = false,
	onChange = () => null,
	showLabel = true,
	onSaveSignedPdf = null,
	canViewSignedPdf = false,
	canSignPdf = false,
	canPreviewPdf = false,
	canDeleteSignedPdf = false,
	onDeleteSignedPdf = null,
}) {
	const { t } = useTranslation();
	const [list, setList] = useState(initData || []);
	const [uploadingFile, setUploadingFile] = useState(false);
	const [removingId, setRemovingId] = useState(null);
	const [removingSignedPdfId, setRemovingSignedPdfId] = useState(null);

	useEffect(() => setList(initData), [initData]);

	function addNewFile() {
		const tempFiles = [
			...list,
			{
				no: list?.length === 0 ? 1 : list[list?.length - 1]?.no + 1,
				id: uuidv4(),
				label: '',
				tempFile: null,
				file: null,
				tempPdf: null,
				pdf: null,
				tempSignedPdf: null,
				signedPdf: null,
			},
		];
		setList(tempFiles);
		onChange(tempFiles);
	}

	function removeFile(id) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id !== item?.id) {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function onLabelChange(id, label) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					label,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function onFileChange(id, fileId) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					tempFile: fileId,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function onPdfChange(id, fileId) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					tempPdf: fileId,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function onSignedPdfChange(id, fileId) {
		if (!onSaveSignedPdf) return Promise.resolve();
		// Parent (NPEditor) persists the signed pdf server-side and refetches the NP,
		// so the fresh signedPdf.url flows back down through initData — no local patch needed.
		return onSaveSignedPdf(id, fileId);
	}

	function onRemoveFile(id) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					tempFile: null,
					file: null,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function onRemovePdf(id) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					tempPdf: null,
					pdf: null,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function removeUploadedFile(id) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					file: null,
					tempFile: null,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function removeUploadedPdf(id) {
		let tempRes = [];
		list?.forEach((item) => {
			if (id === item?.id) {
				tempRes.push({
					...item,
					pdf: null,
					tempPdf: null,
				});
			} else {
				tempRes.push(item);
			}
		});
		setList(tempRes);
		onChange(tempRes);
	}

	function inputItem(item, i, count) {
		return (
			<SimpleGrid key={item?.id} columns={{ base: 1, md: 4 }} spacingX={4} spacingY={1} mb={i === count - 1 ? 0 : 4}>
				<Box>
					<LabelDetail
						direction="column"
						label={showLabel ? 'Label' : null}
						childDetail={<Input mt="1" size="sm" value={item?.label} onChange={(e) => onLabelChange(item?.id, e?.currentTarget.value)} />}
					/>
				</Box>
				<Box>
					<LabelDetail
						direction="column"
						label={showLabel ? 'File (office)' : null}
						childDetail={
							<>
								{item?.file?.url ? (
									<Box ml="-4">
										<WrapItem className="avatarWraper">
											<FontAwesomeIcon
												onClick={() => removeUploadedFile(item?.id)}
												className="remove"
												icon={faMinusSquare}
												style={{
													fontSize: 18,
													display: 'none',
													position: 'absolute',
													zIndex: '100',
													marginLeft: 16,
													color: 'red',
													backgroundColor: 'white',
													padding: 2,
													cursor: 'pointer',
												}}
											/>
											<DownloadAndViewButton url={item?.file?.url} size={item?.file?.size} />
										</WrapItem>
									</Box>
								) : (
									<TempFileUploader
										callBack={(e) => onFileChange(item?.id, e?.id)}
										label={null}
										required={true}
										onCancelFile={() => onRemoveFile(item?.id)}
										callBackUploading={(e) => setUploadingFile(e)}
										disabled={uploadingFile}
										mt={0}
										mb={0}
										size="sm"
										accept=".xls,.xlsx,.doc,.docx,.ppt,.pptx"
									/>
								)}
							</>
						}
						guide={
							<Tip
								childDescription={
									<Box>
										<Text fontSize="sm" fontWeight="600" color="blue.500">
											{t('File Type')}
										</Text>
										<UnorderedList>
											<ListItem>
												<Text fontSize="sm" fontWeight="700" color="blue.500">
													{'doc, docx'}
												</Text>
											</ListItem>
											<ListItem>
												<Text fontSize="sm" fontWeight="700" color="blue.500">
													{'xls, xlsx'}
												</Text>
											</ListItem>
											<ListItem>
												<Text fontSize="sm" fontWeight="700" color="blue.500">
													{'ppt, pptx'}
												</Text>
											</ListItem>
										</UnorderedList>
									</Box>
								}
							/>
						}
					/>
				</Box>
				<Box>
					<LabelDetail
						direction="column"
						label={showLabel ? 'File (pdf)' : null}
						childDetail={
							<>
								{item?.pdf?.url ? (
									<Box ml="-4">
										<WrapItem className="avatarWraper">
											<FontAwesomeIcon
												onClick={() => removeUploadedPdf(item?.id)}
												className="remove"
												icon={faMinusSquare}
												style={{
													fontSize: 18,
													display: 'none',
													position: 'absolute',
													zIndex: '100',
													marginLeft: 16,
													color: 'red',
													backgroundColor: 'white',
													padding: 2,
													cursor: 'pointer',
												}}
											/>
											<DownloadAndViewButton url={item?.pdf?.url} size={item?.pdf?.size} />
										</WrapItem>
									</Box>
								) : (
									<TempFileUploader
										callBack={(e) => onPdfChange(item?.id, e?.id)}
										label={null}
										required={true}
										onCancelFile={() => onRemovePdf(item?.id)}
										callBackUploading={(e) => setUploadingFile(e)}
										disabled={uploadingFile}
										mt={0}
										mb={0}
										size="sm"
										accept="application/pdf"
									/>
								)}
							</>
						}
						guide={
							<Tip
								childDescription={
									<Box>
										<Text fontSize="sm" fontWeight="600" color="blue.500">
											{t('File Type')}
										</Text>
										<UnorderedList>
											<ListItem>
												<Text fontSize="sm" fontWeight="700" color="blue.500">
													{'pdf'}
												</Text>
											</ListItem>
										</UnorderedList>
									</Box>
								}
							/>
						}
					/>
				</Box>
				<Box>
					<Button
						onMouseDown={() => {
							if (item?.tempFile || item?.label || item?.file?.url || item?.pdf?.url) {
								setRemovingId(item?.id);
							} else {
								removeFile(item?.id);
							}
						}}
						colorScheme="blue"
						variant="outline"
						size="sm"
						mt={showLabel ? 5 : 0}
					>
						{t('Remove')}
					</Button>
				</Box>
			</SimpleGrid>
		);
	}

	return (
		<>
			{isEditing ? (
				<Box>
					{showLabel && (
						<Box mt="6">
							<Text mt="4" fontSize="lg" mb="2" fontWeight="bold">
								{t('Other Attachments')}
							</Text>
						</Box>
					)}
					{list.map((item, i) => inputItem(item, i, list?.length))}
					<Button onMouseDown={() => addNewFile()} colorScheme="blue" variant="outline" size="sm" mt={4}>
						{t('Add other attachments')}
					</Button>
				</Box>
			) : (
				<>
					{list?.length > 0 && (
						<Box>
							{showLabel && (
								<Box mt="6">
									<Text mt="4" fontSize="lg" mb="2" fontWeight="bold">
										{t('Other Attachments')}
									</Text>
								</Box>
							)}
							{list?.map((item, i) => {
								return (
									<Box key={i}>
										{(item?.label || item?.file?.url || item?.pdf?.url) && (
											<SimpleGrid spacingX={4} spacingY={1} columns={{ base: 1, md: 4 }} mb={i === list?.length - 1 ? 0 : 4}>
												<LabelDetail
													mt="0"
													mb="0"
													direction="column"
													label="Label"
													guide={null}
													childDetail={<Text>{item?.label}</Text>}
												/>
												{item?.file?.url && (
													<LabelDetail
														mt="0"
														mb="0"
														direction="column"
														label="File (office)"
														guide={
															<Tip
																childDescription={
																	<Box>
																		<Text fontSize="sm" fontWeight="600" color="blue.500">
																			{t('File Type')}
																		</Text>
																		<UnorderedList>
																			<ListItem>
																				<Text fontSize="sm" fontWeight="700" color="blue.500">
																					{'doc, docx'}
																				</Text>
																			</ListItem>
																			<ListItem>
																				<Text fontSize="sm" fontWeight="700" color="blue.500">
																					{'xls, xlsx'}
																				</Text>
																			</ListItem>
																			<ListItem>
																				<Text fontSize="sm" fontWeight="700" color="blue.500">
																					{'ppt, pptx'}
																				</Text>
																			</ListItem>
																		</UnorderedList>
																	</Box>
																}
															/>
														}
														childDetail={<DownloadAndViewButton marginLeft={0} url={item?.file?.url} size={item?.file?.size} />}
													/>
												)}
												{item?.pdf?.url && (
													<LabelDetail
														mt="0"
														mb="0"
														direction="column"
														label="File (pdf)"
														guide={
															<Tip
																childDescription={
																	<Box>
																		<Text fontSize="sm" fontWeight="600" color="blue.500">
																			{t('File Type')}
																		</Text>
																		<UnorderedList>
																			<ListItem>
																				<Text fontSize="sm" fontWeight="700" color="blue.500">
																					{'pdf'}
																				</Text>
																			</ListItem>
																		</UnorderedList>
																	</Box>
																}
															/>
														}
														childDetail={
															<DownloadAndViewButton
																marginLeft={0}
																url={item?.pdf?.url}
																size={item?.pdf?.size}
																showSign={canSignPdf}
																canPreview={canPreviewPdf}
																onSaveSigned={canSignPdf ? ({ id }) => onSignedPdfChange(item?.id, id) : null}
															/>
														}
													/>
												)}
												{canViewSignedPdf && item?.signedPdf?.url && (
													<LabelDetail
														mt="0"
														mb="0"
														direction="column"
														label={t('Signed file (pdf)')}
														guide={
															<Tip
																childDescription={
																	<Box>
																		<Text fontSize="sm" fontWeight="600" color="blue.500">
																			{t('File Type')}
																		</Text>
																		<UnorderedList>
																			<ListItem>
																				<Text fontSize="sm" fontWeight="700" color="blue.500">
																					{'pdf'}
																				</Text>
																			</ListItem>
																		</UnorderedList>
																	</Box>
																}
															/>
														}
														childDetail={
															canDeleteSignedPdf ? (
																<Box ml="-4">
																	<WrapItem className="avatarWraper">
																		<FontAwesomeIcon
																			onClick={() => setRemovingSignedPdfId(item?.id)}
																			className="remove"
																			icon={faMinusSquare}
																			style={{
																				fontSize: 18,
																				display: 'none',
																				position: 'absolute',
																				zIndex: '100',
																				marginLeft: 16,
																				color: 'red',
																				backgroundColor: 'white',
																				padding: 2,
																				cursor: 'pointer',
																			}}
																		/>
																		<DownloadAndViewButton
																			url={item?.signedPdf?.url}
																			size={item?.signedPdf?.size}
																			showSign={false}
																		/>
																	</WrapItem>
																</Box>
															) : (
																<DownloadAndViewButton
																	marginLeft={0}
																	url={item?.signedPdf?.url}
																	size={item?.signedPdf?.size}
																	showSign={false}
																/>
															)
														}
													/>
												)}
											</SimpleGrid>
										)}
									</Box>
								);
							})}
						</Box>
					)}
				</>
			)}
			<AlertDialog isOpen={removingId ? true : false} onClose={() => setRemovingId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure to remove other attachments?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemovingId(null);
									removeFile(removingId);
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
			<AlertDialog isOpen={removingSignedPdfId ? true : false} onClose={() => setRemovingSignedPdfId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure to remove signed pdf?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} onClick={() => setRemovingSignedPdfId(null)}>
								{t('No')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									const id = removingSignedPdfId;
									setRemovingSignedPdfId(null);
									onDeleteSignedPdf?.(id);
								}}
							>
								{t('Yes')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}
